import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Alert, Table, Spinner, Badge, Image } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { childImagesAPI } from '../utils/api';
import type { ChildImage } from '../types/child';
import FaceCapture from './FaceCapture';

interface ChildImagesModalProps {
  show: boolean;
  onHide: () => void;
  nurseryId: string;
  childId: number;
  childName: string;
  onSuccess?: () => void;
}

const ChildImagesModal: React.FC<ChildImagesModalProps> = ({ 
  show, 
  onHide, 
  nurseryId, 
  childId,
  childName,
  onSuccess
}) => {
  const { t } = useTranslation('components', { keyPrefix: 'childImagesModal' });

  const [images, setImages] = useState<ChildImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showCapture, setShowCapture] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<ChildImage | null>(null);
  
  // Form for single upload/edit
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingImage, setEditingImage] = useState<ChildImage | null>(null);
  const [formData, setFormData] = useState({
    file: null as File | null,
    pose: 'front' as ChildImage['pose'],
    image_rank: 1,
    embedding: '[]'
  });

  const fetchImages = useCallback(async () => {
    if (!nurseryId || !childId) return;
    try {
      setLoading(true);
      setError(null);
      const response = await childImagesAPI.listChildImages(nurseryId, childId.toString());
      setImages(response.data || []);
    } catch (err) {
      console.error('Error fetching images:', err);
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        setImages([]);
      } else {
        setError(t('failedToLoadImages'));
      }
    } finally {
      setLoading(false);
    }
  }, [nurseryId, childId, t]);

  useEffect(() => {
    if (show) {
      setError(null);
      fetchImages();
    }
  }, [show, fetchImages]);

  const handleShowDeleteModal = (img: ChildImage) => {
    setImageToDelete(img);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setImageToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!imageToDelete) return;
    try {
      setLoading(true);
      await childImagesAPI.deleteChildImage(nurseryId, childId, imageToDelete.id);
      handleCloseDeleteModal();
      await fetchImages();
      onSuccess?.();
    } catch (err) {
      console.error('Error deleting image:', err);
      setError(t('failedToDeleteImage'));
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.file && !editingImage) return;

    try {
      setUploading(true);
      const data = new FormData();
      if (formData.file) {
        data.append('file', formData.file);
        data.append('embedding', formData.embedding);
      }
      data.append('pose', formData.pose);
      data.append('image_rank', formData.image_rank.toString());

      if (editingImage) {
        await childImagesAPI.updateChildImage(nurseryId, childId.toString(), editingImage.id, data);
      } else {
        await childImagesAPI.uploadChildImage(nurseryId, childId.toString(), data);
      }

      setShowUploadModal(false);
      setEditingImage(null);
      setFormData({ file: null, pose: 'front', image_rank: 1, embedding: '[]' });
      await fetchImages();
      onSuccess?.();
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(t('failedToUploadImage'));
    } finally {
      setUploading(false);
    }
  };

  const handleCaptureComplete = async (capturedImages: Array<{ file: File; pose: string; image_rank: number }>) => {
    try {
      setUploading(true);
      setShowCapture(false);
      
      for (const img of capturedImages) {
        const data = new FormData();
        data.append('file', img.file);
        data.append('pose', img.pose);
        data.append('image_rank', img.image_rank.toString());
        data.append('embedding', '[]');
        await childImagesAPI.uploadChildImage(nurseryId, childId.toString(), data);
      }
      
      await fetchImages();
      onSuccess?.();
    } catch (err) {
      console.error('Error uploading captured images:', err);
      setError(t('failedToUploadCaptured'));
    } finally {
      setUploading(false);
    }
  };

  const openUploadModal = (img?: ChildImage) => {
    if (img) {
      setEditingImage(img);
      setFormData({
        file: null,
        pose: img.pose,
        image_rank: img.image_rank,
        embedding: '[]'
      });
    } else {
      setEditingImage(null);
      setFormData({
        file: null,
        pose: 'front',
        image_rank: images.length + 1,
        embedding: '[]'
      });
    }
    setShowUploadModal(true);
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t('manageImages', { childName })}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
          {uploading && (
            <Alert variant="info">
              <Spinner animation="border" size="sm" className="me-2" />
              {t('uploadingPleaseWait')}
            </Alert>
          )}
          
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6>{t('childImages', { count: images.length })}</h6>
            <div className="d-flex gap-2">
              <Button variant="outline-primary" size="sm" onClick={() => setShowCapture(true)}>
                {t('faceScanner')}
              </Button>
              <Button variant="primary" size="sm" onClick={() => openUploadModal()}>
                {t('uploadImage')}
              </Button>
            </div>
          </div>

          {loading && images.length === 0 ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>{t('thumbnail')}</th>
                    <th>{t('pose')}</th>
                    <th>{t('rank')}</th>
                    <th>{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {images.length > 0 ? (
                    images.map((img) => (
                      <tr key={img.id}>
                        <td style={{ width: '100px' }}>
                          <Image 
                            src={img.public_url || (img.image_path.startsWith('http') ? img.image_path : `${import.meta.env.VITE_API_URL || ''}${img.image_path}`)} 
                            thumbnail 
                            style={{ maxWidth: '80px', maxHeight: '80px' }}
                          />
                        </td>
                        <td>
                          <Badge bg="info">{img.pose != 'profile'? img.pose : 'front'}</Badge>
                        </td>
                        <td>{img.image_rank}</td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button variant="outline-secondary" size="sm" onClick={() => openUploadModal(img)}>
                              {t('edit')}
                            </Button>
                            <Button variant="outline-danger" size="sm" onClick={() => handleShowDeleteModal(img)}>
                              {t('delete')}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="text-center py-4">{t('noImages')}</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>{t('close')}</Button>
        </Modal.Footer>
      </Modal>

      {/* Upload/Edit Modal */}
      <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editingImage ? t('editImage') : t('uploadImage')}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUploadSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>{editingImage ? t('imageFileOptional') : t('imageFileRequired')}</Form.Label>
              <Form.Control 
                type="file" 
                accept="image/*" 
                onChange={(e: any) => setFormData({ ...formData, file: e.target.files[0] })}
                required={!editingImage}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('poseLabel')}</Form.Label>
              <Form.Select 
                value={formData.pose} 
                onChange={(e: any) => setFormData({ ...formData, pose: e.target.value })}
              >
                <option value="front">{t('front')}</option>
                <option value="left-side">{t('leftSide')}</option>
                <option value="right-side">{t('rightSide')}</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('rankRange')}</Form.Label>
              <Form.Control 
                type="number" 
                min="1" 
                max="10" 
                value={formData.image_rank} 
                onChange={(e) => setFormData({ ...formData, image_rank: parseInt(e.target.value) })}
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowUploadModal(false)}>{t('cancel')}</Button>
            <Button variant="primary" type="submit" disabled={uploading}>
              {uploading ? <Spinner animation="border" size="sm" /> : (editingImage ? t('update') : t('upload'))}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Face Capture Component */}
      {showCapture && (
        <FaceCapture 
          show={showCapture} 
          onHide={() => setShowCapture(false)} 
          onComplete={handleCaptureComplete}
          startingRank={images.length + 1}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('confirmDelete')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('confirmDeleteMessage', { rank: imageToDelete?.image_rank, pose: imageToDelete?.pose })}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCloseDeleteModal} disabled={loading}>
            {t('cancel')}
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : t('deleteImage')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ChildImagesModal;
